import { RedeemClient } from '@manifoldxyz/redeem-client';

export const APPS_BACKEND = 'https://apps.api.manifoldxyz.dev';
export const REDEEM_BACKEND = 'https://redeem.api.manifoldxyz.dev';
export const appsApiBaseUrl =
  process.env.NODE_ENV === 'development' && process.env.VUE_APP_APPS_BACKEND
    ? process.env.VUE_APP_APPS_BACKEND
    : `${APPS_BACKEND}`;

export const redeemApiBaseUrl =
  process.env.NODE_ENV === 'development' && process.env.VUE_APP_REDEEM_BACKEND
    ? process.env.VUE_APP_REDEEM_BACKEND
    : `${REDEEM_BACKEND}`;

export type GiveawayRequirement = {
  index: number;
  userData: any;
};

abstract class APIHelpers {
  protected baseUrl: string;

  protected async _getHeaders() {
    const headers = {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(
        `manifoldOAuth:${localStorage.getItem('connectedAddress')?.toLowerCase()}`
      )}`,
    } as any;

    return headers;
  }

  protected async _fetch(path: string, options: any = {}) {
    try {
      let url = this.baseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }

      const requestOptions = {
        method: 'GET',
        headers: await this._getHeaders(),
        ...options,
      };

      const response = await fetch(url, requestOptions);
      if (response.status !== 200) {
        throw response;
      }

      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error.json) {
        const response = error;
        let result;
        try {
          result = await response.json();
        } catch (e) {
          throw response;
        }

        throw result;
      } else {
        throw error;
      }
    }
  }

  protected async _put(path: string, options: any = {}) {
    try {
      let url = this.baseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }

      const requestOptions = {
        method: 'PUT',
        headers: await this._getHeaders(),
        ...options,
      } as any;

      if (options.body) {
        requestOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, requestOptions);
      if (response.status !== 200) {
        throw response;
      }
      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error.json) {
        const response = error;
        try {
          const result = await response.json();
          throw result;
        } catch (e) {
          throw response;
        }
      } else {
        throw error;
      }
    }
  }

  protected async _patch(path: string, options: any = {}) {
    try {
      let url = this.baseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }

      const requestOptions = {
        method: 'PATCH',
        headers: await this._getHeaders(),
        ...options,
      } as any;

      if (options.body) {
        requestOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, requestOptions);
      if (response.status !== 200) {
        throw response;
      }
      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error.json) {
        const response = error;
        try {
          const result = await response.json();
          throw result;
        } catch (e) {
          throw response;
        }
      } else {
        throw error;
      }
    }
  }

  protected async _post(path: string, options: any = {}) {
    try {
      let url = this.baseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }
      const requestOptions = {
        method: 'POST',
        headers: await this._getHeaders(),
        ...options,
      } as any;

      if (options.body) {
        requestOptions.body = JSON.stringify(options.body);
      }

      const response = await fetch(url, requestOptions);
      if (response.status !== 200) {
        throw response;
      }
      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error.json) {
        const response = error;
        try {
          const result = await response.json();
          throw result;
        } catch (e) {
          throw response;
        }
      } else {
        throw error;
      }
    }
  }

  protected async _delete(path: string, options: any = {}) {
    try {
      let url = this.baseUrl + path;

      if (options.query) {
        const queryString = new URLSearchParams(options.query).toString();
        url = url + '?' + queryString;
      }

      const headers = await this._getHeaders();
      delete headers['Content-Type'];

      const requestOptions = {
        method: 'DELETE',
        headers,
        ...options,
      } as any;

      const response = await fetch(url, requestOptions);
      if (response.status !== 200) {
        throw response;
      }
      const result = await response.json();

      return result;
    } catch (error: any) {
      if (error.json) {
        const response = error;
        try {
          const result = await response.json();
          throw result;
        } catch (e) {
          throw response;
        }
      } else {
        throw error;
      }
    }
  }
}

/**
 * For apps api
 */
class AppsApi extends APIHelpers {
  constructor() {
    super();
    this.baseUrl = appsApiBaseUrl;
  }

  /**
   * Get an instance by id
   */
  async getInstance(instanceId: number) {
    return this._fetch(
      `/public/instance/data?id=${instanceId}&mediaPreview=true&maxMediaWidth=1024`
    );
  }
}

/**
 * For redeem-server api
 */
class RedeemApi {
  public clientLib: RedeemClient;

  constructor() {
    this.clientLib = new RedeemClient({
      baseUrl: redeemApiBaseUrl,
      getHeaders: this._getHeaders,
      defaultTimeout: 30000,
    });
  }

  protected _getHeaders() {
    const headers = {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${localStorage.getItem(
        `manifoldOAuth:${localStorage.getItem('connectedAddress')?.toLowerCase()}`
      )}`,
    };

    return headers;
  }
}

const appsApi = new AppsApi();
const redeemApi = new RedeemApi();

export { appsApi, redeemApi };
